import React from "react";
import "./_opc-skeleton.scss";

// ═══════════════════════════════════════════════════════════════════════════
// First-load skeletons for the coach screens (CD26): always a skeleton shaped
// like the screen, never a spinner.
//   dashboard  overview + badges + company cards
//   company    header + member rows
//   plan       header + fact tiles + two columns of cards
//   split      chat ‖ document (check-in, review)
//   chat       read-only chat bubbles
// ═══════════════════════════════════════════════════════════════════════════

export const OpcSk = ({ w = "100%", h = 12, r = 6, className = "" }) => (
  <span
    className={`opc-sk ${className}`}
    style={{
      width: typeof w === "number" ? `${w}px` : w,
      height: h,
      borderRadius: r,
    }}
    aria-hidden="true"
  />
);

const Lines = ({ widths }) => (
  <div className="opc-sk-lines">
    {widths.map((w, i) => (
      <OpcSk key={i} w={w} h={11} />
    ))}
  </div>
);

const Bubbles = () => (
  <div className="opc-sk-chat">
    {[
      ["left", "65%", 3],
      ["right", "45%", 2],
      ["left", "72%", 4],
    ].map(([side, width, lines], i) => (
      <div className={`opc-sk-chat__msg is-${side}`} key={i}>
        {side === "left" ? <OpcSk w={28} h={28} r={14} /> : null}
        <div className="opc-sk-chat__bubble" style={{ width }}>
          {Array.from({ length: lines }).map((_, j) => (
            <OpcSk key={j} w={j === lines - 1 ? "55%" : "100%"} h={10} />
          ))}
        </div>
      </div>
    ))}
  </div>
);

const Dashboard = () => (
  <div className="opc-sk-page">
    <Lines widths={[120, 260]} />
    <div className="opc-sk-row">
      <OpcSk w={150} h={36} r={8} />
      <OpcSk w={150} h={36} r={8} />
      <OpcSk w={180} h={36} r={8} />
    </div>
    <div className="opc-sk-grid">
      {[0, 1, 2].map((i) => (
        <div className="opc-sk-card" key={i}>
          <div className="opc-sk-row">
            <OpcSk w={36} h={36} r={18} />
            <OpcSk w="50%" h={14} />
          </div>
          <div className="opc-sk-row opc-sk-row--spread">
            <OpcSk w={60} h={28} />
            <OpcSk w={60} h={28} />
            <OpcSk w={60} h={28} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const Company = () => (
  <div className="opc-sk-page">
    <div className="opc-sk-row opc-sk-row--spread">
      <Lines widths={[180, 300]} />
      <OpcSk w={180} h={36} r={8} />
    </div>
    {[0, 1, 2].map((i) => (
      <div className="opc-sk-card" key={i}>
        <div className="opc-sk-row opc-sk-row--spread">
          <div className="opc-sk-row">
            <OpcSk w={36} h={36} r={18} />
            <OpcSk w={180} h={14} />
          </div>
          <OpcSk w={220} h={28} r={6} />
        </div>
      </div>
    ))}
  </div>
);

const Plan = () => (
  <div className="opc-sk-page">
    <Lines widths={[240, "60%", 320]} />
    <div className="opc-sk-grid opc-sk-grid--4">
      {[0, 1, 2, 3].map((i) => (
        <OpcSk key={i} h={72} r={12} />
      ))}
    </div>
    <div className="opc-sk-grid opc-sk-grid--2">
      {[0, 1].map((i) => (
        <div className="opc-sk-card" key={i}>
          <OpcSk w={140} h={14} />
          <Lines widths={["90%", "75%", "82%", "60%"]} />
        </div>
      ))}
    </div>
  </div>
);

const Split = () => (
  <div className="opc-sk-split">
    <div className="opc-sk-split__left">
      <Bubbles />
      <OpcSk h={96} r={24} />
    </div>
    <div className="opc-sk-split__right">
      <div className="opc-sk-row opc-sk-row--spread">
        <Lines widths={[160, 260]} />
        <OpcSk w={140} h={28} r={8} />
      </div>
      <OpcSk h={80} r={10} />
      <Lines widths={["30%", "95%", "88%", "70%"]} />
      <Lines widths={["25%", "90%", "84%"]} />
    </div>
  </div>
);

// CD50: the right-hand document of a split page (check-in report, review
// panel) while it loads — goes in `OpcSplitPanels` beside `OpcChatSkeleton`.
export const OpcPanelSkeleton = () => (
  <div className="opc-sk-panel" aria-busy="true" aria-label="Loading">
    <div className="opc-sk-row opc-sk-row--spread">
      <Lines widths={[220, 300]} />
      <OpcSk w={140} h={32} r={8} />
    </div>
    <OpcSk h={96} r={10} />
    <Lines widths={["30%", "95%", "88%", "70%"]} />
    <Lines widths={["25%", "90%", "84%", "60%"]} />
  </div>
);

const Chat = () => (
  <div className="opc-sk-page opc-sk-page--full">
    <Bubbles />
  </div>
);

const SHAPES = {
  dashboard: Dashboard,
  company: Company,
  plan: Plan,
  split: Split,
  chat: Chat,
};

const OpcSkeleton = ({ kind = "plan" }) => {
  const Shape = SHAPES[kind] || Plan;
  return (
    <div className="opc-sk-wrap" aria-busy="true" aria-label="Loading">
      <Shape />
    </div>
  );
};

export default OpcSkeleton;
